import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useTranslation } from "react-i18next";
import { getTicketStatusLabel } from "app/utils/tickets/getTicketStatusLabel";
import { getTicketValidityDisplay } from "app/utils/tickets/getTicketValidityDisplay";

const normalizeStatus = (raw) => String(raw || "").trim().toLowerCase();

const getStatusChipProps = (status) => {
  switch (status) {
    case "active":
      return { color: "success" };
    case "ready":
      return { color: "info" };
    case "unpaid":
      return {
        color: "warning",
        sx: {
          bgcolor: "#FDD835",
          color: "#000",
        },
      };
    case "expired":
      return { color: "warning" };
    case "cancelled":
    case "canceled":
    case "refunded":
    case "blocked":
      return { color: "error" };
    default:
      return { color: "default" };
  }
};

const resolveTicketId = (ticket) =>
  ticket?.ticketId ??
  ticket?._id ??
  ticket?.id ??
  ticket?.ticket_id ??
  ticket?.ticket?._id ??
  null;

const TicketCard = ({ ticket, onSelect, onPrintTicket }) => {
  const { t } = useTranslation();

  const ticketId = resolveTicketId(ticket);
  const ticketCode = ticket?.ticketCode ?? ticket?.code ?? ticket?.ticket_code ?? "";
  const statusKey = normalizeStatus(ticket?.status);
  const chipProps = getStatusChipProps(statusKey);
  const statusLabel = getTicketStatusLabel(t, statusKey);
  const validity = getTicketValidityDisplay(t, ticket, { withDateTime: true });
  const resolveI18nString = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "object") return value.sk || value.en || null;
    if (value === "[object Object]") return null;
    return value;
  };

  const productName =
    resolveI18nString(ticket?.productSnapshot?.name) ??
    resolveI18nString(ticket?.productSnapshot?.title) ??
    resolveI18nString(ticket?.productSnapshot?.label) ??
    "—";
  const orderId = ticket?.orderId ?? ticket?.order_id ?? "";
  const canPrint = statusKey === "ready" && Boolean(onPrintTicket);

  const clickable = Boolean(ticketId);

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        overflow: "hidden",
        ...(clickable
          ? { "&:hover": { boxShadow: 2, borderColor: "text.secondary" } }
          : null),
      }}
    >
      <CardActionArea
        disabled={!clickable}
        onClick={() => {
          if (!ticketId) return;
          onSelect?.(ticket);
        }}
        sx={{
          height: "100%",
          p: 2,
          cursor: clickable ? "pointer" : "default",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Stack spacing={1.25} sx={{ width: "100%" }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box minWidth={0}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ wordBreak: "break-word" }}>
                {productName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("pages.dsokDetails.accessSystem.summary.ticketCode", {
                  defaultValue: "Kód lístka",
                })}
                {": "}
                <Box component="span" sx={{ fontFamily: "monospace" }}>
                  {ticketCode || "—"}
                </Box>
              </Typography>
            </Box>

            <Chip
              size="small"
              label={statusLabel}
              color={chipProps.color}
              variant={chipProps.color === "default" ? "outlined" : "filled"}
              sx={{ flex: "0 0 auto", ...(chipProps.sx || {}) }}
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t("pages.dsokDetails.accessSystem.summary.validityRules", {
                  defaultValue: "Platnosť",
                })}
              </Typography>
              <Typography variant="body2">{validity.primary}</Typography>
              {validity.range ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {validity.range}
                </Typography>
              ) : null}
              {validity.secondary ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {validity.secondary}
                </Typography>
              ) : null}
              {orderId ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("pages.dsokDetails.accessSystem.results.columns.orderId", {
                    defaultValue: "Objednávka",
                  })}
                  {": "}
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {orderId}
                  </Box>
                </Typography>
              ) : null}
            </Box>

            {canPrint ? (
              <IconButton
                size="small"
                color="primary"
                aria-label={t("pages.dsokDetails.accessSystem.detail.actions.print.selectPrinterTitle", {
                  defaultValue: "Tlačiť lístok",
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  onPrintTicket?.(ticket);
                }}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>

          {!clickable ? (
            <Typography variant="caption" color="text.secondary">
              {t("pages.dsokDetails.accessSystem.results.missingTicketId", {
                defaultValue: "Chýba ID lístka",
              })}
            </Typography>
          ) : null}
        </Stack>
      </CardActionArea>
    </Card>
  );
};

const TicketSearchResultsGrid = ({ items, onSelectTicket, onPrintTicket }) => {
  const safeItems = Array.isArray(items) ? items : [];
  if (safeItems.length === 0) return null;

  return (
    <Grid container spacing={2}>
      {safeItems.map((it, idx) => {
        const ticketId = resolveTicketId(it);
        return (
          <Grid item key={String(ticketId || idx)} xs={12} sm={6} md={4}>
            <TicketCard
              ticket={it}
              onSelect={onSelectTicket}
              onPrintTicket={onPrintTicket}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default TicketSearchResultsGrid;

